import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FirestoreService } from '../services/firestore.service';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NativeBridgeService } from '../services/native-bridge.service';
import { CelebrationService } from '../services/celebration.service';
import { BreakyComponent } from '../shared/components/breaky/breaky.component';
import { BreakyPose } from '../shared/breaky/breaky-poses';
import { ImpactStyle, NotificationType } from '@capacitor/haptics';

interface MoodOption {
  key: string;
  emoji: string;
  label: string;
  pose: BreakyPose;
  /** Lo que dice Breaky al elegirlo: cercano y sin consejo medico. */
  text: string;
}

@Component({
  selector: 'app-daily-checkin-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, BreakyComponent],
  templateUrl: './daily-checkin-modal.component.html',
  styleUrls: ['./daily-checkin-modal.component.css']
})
export class DailyCheckinModalComponent {
  @Output() closeModal = new EventEmitter<void>();

  private readonly celebration = inject(CelebrationService);

  readonly moods: MoodOption[] = [
    { key: 'Feliz', emoji: '🙂', label: 'Feliz', pose: 'happy', text: '¡Qué bueno! Que ese ánimo te acompañe todo el día.' },
    { key: 'Neutral', emoji: '😐', label: 'Neutral', pose: 'idle', text: 'Un día tranquilo también está bien. Una pausa te puede dar energía.' },
    { key: 'Triste', emoji: '😞', label: 'Triste', pose: 'sad', text: 'Gracias por contarme. Aquí estoy; una pausa suave puede ayudarte.' },
    { key: 'Estresado', emoji: '😰', label: 'Estresado', pose: 'worried', text: 'Respira hondo. Una pausa de respiración puede darte un respiro.' },
    { key: 'Agotado', emoji: '😫', label: 'Agotado', pose: 'sleep', text: 'Se nota el cansancio. Date permiso para una pausa de descanso.' }
  ];

  selectedMood: string | null = null;
  comment: string = '';
  isSaving = false;

  constructor(
    private firestoreService: FirestoreService,
    private authService: AuthService,
    private router: Router,
    private nativeBridge: NativeBridgeService
  ) {}

  get current(): MoodOption | undefined {
    return this.moods.find((m) => m.key === this.selectedMood);
  }

  get pose(): BreakyPose {
    return this.current?.pose ?? 'wave';
  }

  get bubbleText(): string {
    return this.current?.text ?? 'Toca la cara que mejor te represente.';
  }

  selectMood(mood: string) {
    this.selectedMood = mood;
    this.nativeBridge.hapticImpact(ImpactStyle.Light);
  }

  async saveCheckin() {
    if (!this.selectedMood) {
      return;
    }

    this.isSaving = true;
    try {
      const user = await firstValueFrom(this.authService.currentUser$);
      if (user) {
        const points = await this.firestoreService.saveDailyCheckin(user.uid, this.selectedMood, this.comment);
        await this.nativeBridge.hapticNotification(NotificationType.Success);
        const mood = this.current;
        this.celebration.push({
          kind: 'reward',
          title: '¡Check-in completado!',
          subtitle: mood?.text ?? 'Gracias por contarle a Breaky cómo te sientes.',
          amount: points,
          label: 'Vitalis 🌿',
          pose: mood?.pose === 'sleep' || mood?.pose === 'worried' || mood?.pose === 'sad' ? 'happy' : (mood?.pose ?? 'happy')
        });
        await this.router.navigate(['/dashboard/home']);
        this.closeModal.emit();
      }
    } catch (error) {
      console.error('Error saving checkin:', error);
    } finally {
      this.isSaving = false;
    }
  }
}
